import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Table,
  Button,
  Pagination,
  Alert,
  Spinner,
  Badge,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaReceipt,
  FaSearch,
  FaFilter,
} from 'react-icons/fa';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';

const getAuthToken = () => localStorage.getItem('authToken');

const formatDisplayDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}-${month}-${year}`;
};

const formatAmount = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
};

const ReceiptList = () => {
  const [receipts, setReceipts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const limit = 50;
  const navigate = useNavigate();

  useEffect(() => {
    fetchReceipts(currentPage);
  }, [currentPage]);

  const fetchReceipts = async (page) => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-receipts?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setReceipts(response.data.receipts || []);
      setTotalPages(response.data.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Error fetching receipts:', error);
      setAlert({
        show: true,
        message: 'Error loading receipts. Please try again.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}delete-receipt-v2`,
        { receipt_id: id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setAlert({
        show: true,
        message: response.data.message || 'Receipt deleted successfully!',
        variant: 'success',
      });
      fetchReceipts(currentPage);
    } catch (error) {
      console.error('Error deleting receipt:', error);
      setAlert({
        show: true,
        message:
          error.response?.data?.message ||
          'Error deleting receipt. Please try again.',
        variant: 'danger',
      });
    }
  };

  const filteredReceipts = receipts.filter((receipt) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      receipt.receipt_number?.toString().toLowerCase().includes(search) ||
      receipt.from_account?.toLowerCase().includes(search) ||
      receipt.to_account?.toLowerCase().includes(search) ||
      receipt.description?.toLowerCase().includes(search)
    );
  });

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '18px 10px',
        }}
      >
        {/* Compact Top Bar (like StudentListV2) */}
        <div
          className="compact-top-bar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            marginBottom: '14px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '7px 10px',
            borderRadius: '7px',
            color: 'white',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            <FaReceipt style={{ fontSize: '1.3rem', marginRight: 6 }} />
            <span
              style={{
                fontWeight: 600,
                fontSize: '1.1rem',
                letterSpacing: 0.5,
              }}
            >
              Receipts
            </span>
            <div style={{ position: 'relative', width: 220 }}>
              <FaSearch
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#adb5bd',
                  fontSize: '1rem',
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                placeholder="Search receipts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px 6px 32px',
                  border: '1px solid #495057',
                  borderRadius: '5px',
                  fontSize: '0.95rem',
                  outline: 'none',
                  background: '#f8f9fa',
                  color: '#23272b',
                  transition: 'all 0.2s',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#667eea')}
                onBlur={(e) => (e.target.style.borderColor = '#495057')}
              />
            </div>
            {/* Future: Add more filters here, e.g. date/account */}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button
              variant="light"
              size="sm"
              onClick={() => navigate('/receipt')}
              aria-label="New Receipt"
              style={{
                borderRadius: '6px',
                width: '34px',
                height: '34px',
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="New Receipt"
            >
              <FaPlus />
            </Button>
          </div>
        </div>

        {/* Alert Messages */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Table Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            overflowX: 'auto',
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <Spinner animation="border" variant="primary" />
              <p style={{ marginTop: '15px', color: '#6c757d' }}>
                Loading receipts...
              </p>
            </div>
          ) : filteredReceipts.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#6c757d',
              }}
            >
              <FaReceipt
                style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '15px' }}
              />
              <p style={{ fontSize: '1.1rem', margin: 0 }}>
                {searchTerm
                  ? 'No receipts found matching your search'
                  : 'No receipts available'}
              </p>
            </div>
          ) : (
            <>
              <Table hover responsive style={{ marginBottom: 0 }}>
                <thead
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  <tr>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Date
                    </th>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Receipt #
                    </th>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      Received From
                    </th>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      To Account
                    </th>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'right',
                      }}
                    >
                      Amount
                    </th>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      Description
                    </th>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReceipts.map((receipt) => (
                    <tr
                      key={receipt.id}
                      style={{
                        borderBottom: '1px solid #f0f0f0',
                        transition: 'background-color 0.2s ease',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = '#f8f9ff')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = 'transparent')
                      }
                    >
                      <td
                        style={{
                          padding: '4px 8px',
                          whiteSpace: 'nowrap',
                          color: '#495057',
                        }}
                      >
                        {formatDisplayDate(receipt.date)}
                      </td>
                      <td style={{ padding: '4px 8px', color: '#495057' }}>
                        <Badge
                          bg="secondary"
                          style={{
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            padding: '6px 12px',
                            borderRadius: '6px',
                          }}
                        >
                          #{receipt.receipt_number}
                        </Badge>
                      </td>
                      <td style={{ padding: '4px 8px', color: '#495057' }}>
                        <OverlayTrigger
                          placement="top"
                          delay={{ show: 0, hide: 100 }}
                          overlay={
                            <Tooltip id={`from-account-tooltip-${receipt.id}`}>
                              {receipt.from_account}
                            </Tooltip>
                          }
                        >
                          <span
                            style={{
                              display: 'inline-block',
                              maxWidth: 140,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              verticalAlign: 'middle',
                              cursor: 'pointer',
                            }}
                          >
                            {receipt.from_account}
                          </span>
                        </OverlayTrigger>
                      </td>
                      <td style={{ padding: '4px 8px', color: '#495057' }}>
                        <OverlayTrigger
                          placement="top"
                          delay={{ show: 0, hide: 100 }}
                          overlay={
                            <Tooltip id={`to-account-tooltip-${receipt.id}`}>
                              {receipt.to_account}
                            </Tooltip>
                          }
                        >
                          <span
                            style={{
                              display: 'inline-block',
                              maxWidth: 140,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              verticalAlign: 'middle',
                              cursor: 'pointer',
                            }}
                          >
                            {receipt.to_account}
                          </span>
                        </OverlayTrigger>
                      </td>
                      <td
                        style={{
                          padding: '4px 8px',
                          textAlign: 'right',
                          fontWeight: '600',
                          color: '#28a745',
                          fontSize: '1rem',
                        }}
                      >
                        {formatAmount(receipt.amount)}
                      </td>
                      <td
                        style={{
                          padding: '4px 8px',
                          color: '#6c757d',
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {receipt.description || '-'}
                      </td>
                      <td
                        style={{
                          padding: '4px 8px',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            navigate(`/edit-receipt/${receipt.id}`)
                          }
                          style={{
                            borderRadius: '6px',
                            width: '32px',
                            height: '32px',
                            padding: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '4px',
                          }}
                          title="Edit Receipt"
                          aria-label="Edit Receipt"
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Are you sure you want to delete Receipt #${receipt.receipt_number}?`,
                              )
                            ) {
                              handleDelete(receipt.id);
                            }
                          }}
                          style={{
                            borderRadius: '6px',
                            width: '32px',
                            height: '32px',
                            padding: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title="Delete Receipt"
                          aria-label="Delete Receipt"
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div
                  style={{
                    marginTop: '25px',
                    paddingTop: '20px',
                    borderTop: '1px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Pagination style={{ margin: 0 }}>
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    />
                    {totalPages <= 13 ? (
                      [...Array(totalPages)].map((_, index) => (
                        <Pagination.Item
                          key={index}
                          active={index + 1 === currentPage}
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </Pagination.Item>
                      ))
                    ) : (
                      <>
                        <Pagination.Item
                          active={currentPage === 1}
                          onClick={() => setCurrentPage(1)}
                        >
                          1
                        </Pagination.Item>
                        {currentPage > 6 && <Pagination.Ellipsis disabled />}
                        {Array.from(
                          { length: 11 },
                          (_, i) => currentPage - 5 + i,
                        )
                          .filter((page) => page > 1 && page < totalPages)
                          .map((page) => (
                            <Pagination.Item
                              key={page}
                              active={page === currentPage}
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </Pagination.Item>
                          ))}
                        {currentPage < totalPages - 5 && (
                          <Pagination.Ellipsis disabled />
                        )}
                        <Pagination.Item
                          active={currentPage === totalPages}
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                        </Pagination.Item>
                      </>
                    )}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ReceiptList;
